// Card 24, the imprint (R548, R1852, ui-map-interface.md §17.3.24).
//
// A modern chart carries its number, its edition and "corrected through
// Notice N" in the lower margin; an engraved sheet carries its imprint.
// This one states what a bug report needs and a player never asks for: the
// world, the edition, how many notices this session has seen, the balance
// pack the server applied, the server build and the protocol version.
//
// A screenshot then carries its own provenance. An owner who reads one
// knows which pack drew it.
//
// **The pack and the build ride the wire.** `Hello.build` and `Hello.pack`
// are the two additive fields of R548. A server that can say neither leaves
// them empty, and the line then prints the dashes rather than nothing: a
// missing figure is itself a fact about the server.
//
// **It is ink on the sheet, in the margin** (R1852). The owner ruled on
// 2026-10-09 that the imprint is an imprint on the map and not a line of
// the cartouche plate. It stands below the bottom-left corner of
// the world, in the void, as the margin warnings of card 61 do: its block
// starts `gap_px` outside both edges of the corner, so it keeps clear of
// the neat line and its figures, and every margin warning stands in the
// middle of an edge, far from a corner. It is world-space text, so it shows
// only while the camera shows that corner. Its words take one chart text
// step in both readings (R1796), and the block grows about its top-left
// corner, so the band scale never pushes it onto the world.
//
// **It is STATIC.** It packs on the hello, on a reading change, when the
// Fell face lands and once a world minute, which carries a new notice count
// onto the sheet. It names no `state` reason: that reason fires on every
// State frame, and it would repack the whole sea tier with it.

import { PROTOCOL } from '../../../gen/balance';
import { chart as S } from '../../../gen/strings';
import { toRoman } from '../../../render/roman';
import { WORLD_H } from '../../../world';
import type { FrameInfo } from '../../layer';
import type { ChartContext, ChartSinks, Piece } from '../context';
import { stepPx } from '../steps';
import { CHART_INK, faceOf, inkOf } from './shared';

/** What a line prints for a figure the server did not send. */
const UNSAID = '—';

/** The lines of the imprint in one reading, from the state of the world. */
function imprintLines(ctx: ChartContext): string[] {
  const t = ctx.cfg.pieces.imprint;
  const h = ctx.state.hello;
  const name = h?.worldName ?? '';
  const season = Math.max(1, Math.floor(h?.season ?? 1));
  const notices = ctx.state.noticesSeen;
  // A hex build (a commit) is cut like the pack hash; a release tag stays whole.
  const build = h?.build ? (/^[0-9a-f]{8,}$/.test(h.build) ? h.build.slice(0, t.fig.hash_chars) : h.build) : UNSAID;
  const hash = h?.pack ? h.pack.slice(0, t.fig.hash_chars) : UNSAID;
  // The edition of a sheet is the season it was surveyed in: a world that
  // rolls its season prints a new edition of the same chart.
  return ctx.reading === 'historical'
    ? [
      S.imprint_historical_1({ name, edition: toRoman(season) }),
      S.imprint_historical_2({ notices: toRoman(notices) || String(notices), build }),
    ]
    : [
      S.imprint_modern_1({
        world: h?.worldId ?? 0, edition: season, notices, protocol: PROTOCOL,
      }),
      S.imprint_modern_2({ hash, build }),
    ];
}

/** The top-left corner of the block, in world px: at the left edge of the
 *  world, `gap` px below the bottom edge. */
export function imprintOrigin(gap: number): { x: number; y: number } {
  return { x: 0, y: WORLD_H + gap };
}

export class ImprintPiece implements Piece {
  readonly id = 'imprint' as const;
  readonly tier = 'sea' as const;
  readonly pack = 'static' as const;
  readonly film = 'hide' as const;
  readonly rebuildOn = ['hello', 'reading', 'font', 'minute'] as const;

  /** The lines of the last pack. The tests read them. */
  lines: string[] = [];

  /** A sheet with no hello names no world yet. */
  visible(ctx: ChartContext): boolean { return ctx.state.hello !== null; }

  rebuild(ctx: ChartContext): void {
    this.lines = imprintLines(ctx);
  }

  emit(sinks: ChartSinks, ctx: ChartContext, _frame: FrameInfo): void {
    const t = ctx.cfg.pieces.imprint;
    const r = t[ctx.reading];
    const ink = inkOf(r.ink, CHART_INK.pale);
    const face = faceOf(ctx.reading);
    const step = t.text.line;
    const size = stepPx(face, step);
    const pitch = size * t.fig.line_em;
    // Every line grows about the one corner of the block (R1796), so the
    // lines keep their gap on screen and the block stays in the void.
    const o = imprintOrigin(t.fig.gap_px);
    for (let i = 0; i < this.lines.length; i++) {
      ctx.glyphs.word(sinks.quads, this.lines[i], o.x, o.y + size / 2 + i * pitch, face, step, ink, r.alpha,
        { anchor: 'left', pivot: o });
    }
  }
}

/** The registry's entry. */
export const imprint = new ImprintPiece();
